import { UserAdminItem } from "../interfaces/UserAdminItem";
import SettingsCompany from "./SettingsCompany";

const hasSpecialPermission = (userData: UserAdminItem, extensionName: string): boolean => {
    switch (extensionName) {
        case 'Templates':
            return userData.isTemplateAdmin;
        case 'Pictures':
            return userData.isPictureAdmin;
        case 'Picture Bank':
            return userData.isPictureAdmin;
        case 'Slides':
            return userData.isSlideAdmin;
        case 'Sections':
            return userData.isSectionsAdmin;
        case 'Contents':
            return userData.isContentAdmin;
        case 'Email Signatures':
            return userData.isEmailSignatureAdmin;
        case 'Excel2Web':
            return userData.isExcel2WebAdmin;
        case 'Excel2Word':
            return userData.isExcel2WordAdmin;
        case 'ESEF':
            // ESEF Taxonomist reaches the view too, read-only - see components/esef/Esef.tsx.
            return userData.isESEFAdmin || !!userData.isESEFTaxonomist;
        default:
            return false;
    }
}

const hasSettingsAdminPermission = (userData: UserAdminItem, settings: SettingsCompany, requireSuperAdmin?: boolean): boolean => {
    if (settings.Domain === 'rehngruppen.se' && requireSuperAdmin) {
        return userData.isAdmin;
    }
    else if (settings.Domain === 'rehngruppen.se' && userData.blockRehngruppenAdmin) {
        return false
    }
    else return userData.isSettingsAdmin
}

// Excel2 and ESEF permissions are standalone: they are NOT granted by the general
// settings/contributor/template admin roles the way other extensions are. Access to
// these comes only from the extension's own permission (isExcel2WebAdmin etc.), with
// isAdmin (super) as the usual escape hatch. Their real level model lives apart in
// Excel2Users.json and is administered via the Excel2 Users view.
//
// The flags read here are not the users.json ones: buildUserData overwrites them with
// the inheritance-expanded Excel2Users.json capabilities, so Excel2 Admin already
// carries Web, Word and the whole ESEF stair by the time the menu sees it.
const STANDALONE_EXTENSIONS = ['Excel2Web', 'Excel2Word', 'ESEF'];

// The Excel2 Accounts view administers the whole Excel2 stack (Web, Word, ESEF) out of
// Excel2Users.json. Where a customer has no Excel2 extension at all the view has nothing
// to show, so the menu item is hidden for everyone - super admin included.
const hasAnyExcel2Extension = (settings: SettingsCompany): boolean => {
    return !!settings.Extensions?.some((val) => val.isExcel2);
};

const hasExtensionPermission = (userData: UserAdminItem, extensionName: string, settings: SettingsCompany): boolean => {
    const extension = settings.Extensions?.find((val) => val.ExtensionName === extensionName);
    if (settings.Domain === 'rehngruppen.se' && userData.blockRehngruppenAdmin) {
        return false;
    }
    const hasUserPermission = STANDALONE_EXTENSIONS.includes(extensionName)
        ? (userData.isAdmin || hasSpecialPermission(userData, extensionName))
        : (userData.isTemplateAdmin || userData.isContributorAdmin || userData.isSettingsAdmin || hasSpecialPermission(userData, extensionName));
    const isActivated = extension?.ActivateExcel || extension?.ActivateWord || extension?.ActivatePowerPoint || extension?.ActivateOutlook || extension?.isExcel2;
    const isNonOfficeExtension = !!extension && !extension.ExistInExcel && !extension.ExistInWord && !extension.ExistInPowerPoint && !extension.ExistInOutlook;
    return (hasUserPermission && (isActivated || isNonOfficeExtension)) as boolean;
};

const hasExtensionPermissionForApp = (userData: UserAdminItem, extensionName: string, settings: SettingsCompany, app: 'ActivateWord' | 'ActivateOutlook'): boolean => {
    const extension = settings.Extensions?.find((val) => val.ExtensionName === extensionName);
    if (settings.Domain === 'rehngruppen.se' && userData.blockRehngruppenAdmin) {
        return false;
    }
    return (
        (userData.isTemplateAdmin || userData.isContributorAdmin || userData.isSettingsAdmin || hasSpecialPermission(userData, extensionName)) &&
        !!extension?.[app]
    ) as boolean;
};

const hasFunctionPermission = (userData: UserAdminItem, functionName: string, settings: SettingsCompany): boolean => {
    const func = settings.Functions?.find((val) => val.FunctionName === functionName);
    if (settings.Domain === 'rehngruppen.se' && userData.blockRehngruppenAdmin) {
        return false;
    }
    return (
        (userData.isContributorAdmin || userData.isSettingsAdmin) && !!func?.ActivateFunction
    );
};

const getMenuItems = (userData: UserAdminItem, settings: SettingsCompany, adminMode: boolean): string[] => {
    if (!userData) return [];

    return [
        'home',
        ...(hasExtensionPermission(userData, 'Templates', settings) ? ['templates'] : []),
        ...(hasExtensionPermission(userData, 'Icons', settings) ? ['icons'] : []),
        ...(hasExtensionPermission(userData, 'Pictures', settings) ? ['pictures'] : []),
        ...(hasExtensionPermission(userData, 'Picture Bank', settings) ? ['picturebank'] : []),
        ...(hasExtensionPermission(userData, 'Slides', settings) ? ['slides'] : []),
        ...(hasExtensionPermissionForApp(userData, 'Contents', settings, 'ActivateWord') ? ['wordcontents'] : []),
        ...(hasExtensionPermissionForApp(userData, 'Contents', settings, 'ActivateOutlook') ? ['outlookcontent'] : []),
        ...(hasExtensionPermission(userData, 'Sections', settings) ? ['sections'] : []),
        ...(hasExtensionPermission(userData, 'Email Signatures', settings) ? ['emailsignatures'] : []),
        ...(hasExtensionPermission(userData, 'Tutorials', settings) ? ['tutorials'] : []),
        ...(hasExtensionPermission(userData, 'Excel2Web', settings) ? ['excel2web'] : []),
        ...(hasExtensionPermission(userData, 'AI', settings) ? ['ai'] : []),
        ...(hasFunctionPermission(userData, 'Custom Data', settings) ? ['customdata'] : []),
        ...(hasSettingsAdminPermission(userData, settings, true) ? ['settings'] : []),
        ...(hasFunctionPermission(userData, 'User Information', settings) ? ['userdata'] : []),
        ...(hasFunctionPermission(userData, 'User Data Groups', settings) ? ['userdatagroups'] : []),
        ...(hasSettingsAdminPermission(userData, settings) && hasFunctionPermission(userData, 'Microsoft 365 Groups', settings) ? ['groups'] : []),
        ...(hasSettingsAdminPermission(userData, settings) || userData.isContributorAdmin ? ['statistics'] : []),
        ...(hasSettingsAdminPermission(userData, settings, true) ? ['accounts'] : []),
        ...(hasAnyExcel2Extension(settings) && (userData.isAdmin || userData.isExcel2Admin) ? ['excel2users'] : []),
        ...(adminMode && userData.isAdmin ? ['domains'] : []),
        ...(adminMode && userData.isAdmin ? ['adminaccounts'] : []),
        ...(hasExtensionPermission(userData, 'ESEF', settings) ? ['esef'] : []),
        ...(adminMode && userData.isRehngruppen ? ['customers'] : []),
        ...(adminMode && userData.isRehngruppen ? ['academy'] : []),
    ];
};

export const GetMyMenuItems = (userData: UserAdminItem, settings: SettingsCompany, adminMode: boolean): string[] => {
    return getMenuItems(userData, settings, adminMode);
};

// Every Admin menu page is customer-independent: it routes to /:api/<key> and is
// rendered by Body.tsx without a selected domain (see NON_DOMAIN_PAGES).
export const NON_DOMAIN_PAGES = ['domains', 'adminaccounts', 'news', 'esefadmin', 'aiadmin', 'customers', 'academy'];

// The domain the global admin pages (ESEF Admin, AI Admin) send in the Domain header.
// Their data is global, but the API still needs a real domain to issue a token.
export const ADMIN_DOMAIN = 'rehngruppen.se';

export const AdminMenuItems = (userData: UserAdminItem): { key: string, name: string, url: string }[] => {
    if (!userData) return [];

    const menuItems = [
        { key: 'domains', name: 'Domains', url: '', condition: userData.isAdmin },
        { key: 'adminaccounts', name: 'All Accounts', url: '', condition: userData.isAdmin },
        { key: 'news', name: 'News', url: '', condition: userData.isAdmin },
        { key: 'esefadmin', name: 'ESEF Admin', url: '', condition: userData.isAdmin },
        { key: 'aiadmin', name: 'AI Admin', url: '', condition: userData.isAdmin },
        { key: 'customers', name: 'Customers', url: '', condition: userData.isRehngruppen },
        { key: 'academy', name: 'RehnAcademy', url: '', condition: userData.isRehngruppen },
    ];

    return menuItems.filter(item => item.condition).map(({ condition, ...item }) => item);
};

export const AllMenuItems = (userData: UserAdminItem, settings: SettingsCompany): { key: string, name: string, url: string }[] => {
    if (!userData) return [];

    const menuItems = [
        { key: 'home', name: 'Home', url: '', data: 'Home', condition: true },
        { key: 'templates', name: 'Templates', url: '', condition: hasExtensionPermission(userData, 'Templates', settings) },
        { key: 'icons', name: 'Icons', url: '', condition: hasExtensionPermission(userData, 'Icons', settings) },
        { key: 'pictures', name: 'Pictures', url: '', condition: hasExtensionPermission(userData, 'Pictures', settings) },
        { key: 'picturebank', name: 'Picture Bank', url: '', condition: hasExtensionPermission(userData, 'Picture Bank', settings) },
        { key: 'slides', name: 'Slides', url: '', condition: hasExtensionPermission(userData, 'Slides', settings) },
        { key: 'wordcontents', name: 'Word Content', url: '', condition: hasExtensionPermissionForApp(userData, 'Contents', settings, 'ActivateWord') },
        { key: 'outlookcontent', name: 'Outlook Content', url: '', condition: hasExtensionPermissionForApp(userData, 'Contents', settings, 'ActivateOutlook') },
        { key: 'sections', name: 'Sections', url: '', condition: hasExtensionPermission(userData, 'Sections', settings) },
        { key: 'emailsignatures', name: 'Email Signatures', url: '', condition: hasExtensionPermission(userData, 'Email Signatures', settings) },
        { key: 'tutorials', name: 'Tutorials', url: '', condition: hasExtensionPermission(userData, 'Tutorials', settings) },
        { key: 'excel2web', name: 'Excel2Web', url: '', condition: hasExtensionPermission(userData, 'Excel2Web', settings) },
        { key: 'ai', name: 'AI', url: '', condition: hasExtensionPermission(userData, 'AI', settings) },
        { key: 'customdata', name: 'Custom Data', url: '', condition: hasFunctionPermission(userData, 'Custom Data', settings) },
        { key: 'settings', name: 'Settings', url: '', condition: hasSettingsAdminPermission(userData, settings, true) },
        { key: 'userdata', name: 'User Data', url: '', condition: hasFunctionPermission(userData, 'User Information', settings) },
        { key: 'userdatagroups', name: 'User Data Groups', url: '', condition: hasFunctionPermission(userData, 'User Data Groups', settings) },
        { key: 'groups', name: 'Groups', url: '', condition: hasSettingsAdminPermission(userData, settings) && hasFunctionPermission(userData, 'Microsoft 365 Groups', settings) },
        { key: 'statistics', name: 'Statistics', url: '', condition: hasSettingsAdminPermission(userData, settings) || userData.isContributorAdmin },
        { key: 'accounts', name: 'Accounts', url: '', condition: hasSettingsAdminPermission(userData, settings, true) },
        { key: 'excel2users', name: 'Excel2 Accounts', url: '', condition: hasAnyExcel2Extension(settings) && (userData.isAdmin || !!userData.isExcel2Admin) },
        { key: 'esef', name: 'ESEF', url: '', condition: hasExtensionPermission(userData, 'ESEF', settings) },
    ];

    return menuItems.filter(item => item.condition).map(({ condition, ...item }) => item);
};